/**
 * Decision Options table with STRUCTURAL DISSENT CAPTURE.
 *
 * CD R10 Q3 verdict (HIGHEST priority): "Dissent renders as its own
 * row directly beneath each option — there is no layout in which the
 * pack is readable without the dissent. Explicit anti-SAGE-2020 design."
 *
 * The pattern: every DecisionOption can carry a `dissent` field. When
 * present, the dissent renders as a dedicated row IMMEDIATELY below the
 * option, in its own visual treatment (threat-2 amber tint, "⚑ Minority
 * view" label, dissentingExpert credit). When absent, the table flags
 * "no minority view captured" so reviewers see the absence — never the
 * silent omission that SAGE 2020 took flak for.
 */

import type { DecisionOption } from './types';

interface DecisionOptionsProps {
  options: readonly DecisionOption[];
  /** If true, the "no dissent captured" flag is more prominent (used in
   *  the final-pack-render mode). Default off in builder mode. */
  enforceDissentSurfacing?: boolean;
}

export function DecisionOptions({ options, enforceDissentSurfacing = false }: DecisionOptionsProps) {
  return (
    <div>
      <table style={{
        width: '100%', borderCollapse: 'collapse' as const,
        fontSize: 12.5,
      }}>
        <thead>
          <tr>
            <th style={TH_STYLE}>{' '}</th>
            <th style={TH_STYLE}>Action</th>
            <th style={TH_STYLE}>Cases averted</th>
            <th style={TH_STYLE}>Cost</th>
            <th style={TH_STYLE}>Political</th>
            <th style={TH_STYLE}>Legal authority</th>
          </tr>
        </thead>
        <tbody>
          {options.map(o => (
            <OptionRows key={o.id} option={o} enforceDissentSurfacing={enforceDissentSurfacing} />
          ))}
        </tbody>
      </table>

      <div style={{
        marginTop: 14, padding: '10px 13px',
        fontSize: 12.5, color: 'var(--bm-text-muted)', lineHeight: 1.5,
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderLeft: '3px solid var(--bm-prov-confirmed, var(--bm-state-live))',
        borderRadius: 7,
      }}>
        <b style={{ color: 'var(--bm-text)' }}>Dissent is structural, not optional.</b>{' '}
        Every option's minority view renders as its own row directly beneath it — there is
        no layout in which the pack is readable without the dissent. This is the explicit
        anti-SAGE-2020 design: a reader cannot skip past a disagreement that exists.
      </div>
    </div>
  );
}

const TH_STYLE = {
  textAlign: 'left' as const,
  padding: '8px 10px',
  fontFamily: 'var(--bm-font-mono)', fontSize: 9.5,
  textTransform: 'uppercase' as const, letterSpacing: '.5px',
  color: 'var(--bm-text-subtle)',
  borderBottom: '1px solid var(--bm-border)',
};

const TD_STYLE = {
  padding: '9px 10px',
  borderBottom: '1px solid var(--bm-border)',
  color: 'var(--bm-text-muted)',
  verticalAlign: 'top' as const,
};

function OptionRows({
  option: o, enforceDissentSurfacing,
}: { option: DecisionOption; enforceDissentSurfacing: boolean }) {
  const isRecBg = o.isRecommended ? 'var(--bm-accent-subtle, rgba(110,163,255,0.14))' : undefined;
  const isRecColor = o.isRecommended ? 'var(--bm-text)' : 'var(--bm-text-muted)';
  return (
    <>
      <tr>
        <td style={{ ...TD_STYLE, background: isRecBg, color: 'var(--bm-text)', fontFamily: 'var(--bm-font-mono)', fontWeight: 700 }}>
          {o.id}
          {o.isRecommended && (
            <div style={{
              fontSize: 8.5, color: 'var(--bm-accent, #6ea3ff)', marginTop: 2,
            }}>REC</div>
          )}
        </td>
        <td style={{ ...TD_STYLE, background: isRecBg, color: isRecColor }}>{o.action}</td>
        <td style={{ ...TD_STYLE, background: isRecBg, color: isRecColor, fontFamily: 'var(--bm-font-mono)' }}>{o.averted}</td>
        <td style={{ ...TD_STYLE, background: isRecBg, color: isRecColor, fontFamily: 'var(--bm-font-mono)' }}>{o.cost}</td>
        <td style={{ ...TD_STYLE, background: isRecBg, color: isRecColor }}>{o.political}</td>
        <td style={{ ...TD_STYLE, background: isRecBg, color: isRecColor }}>{o.legal}</td>
      </tr>
      {o.dissent ? (
        <tr>
          <td />
          <td colSpan={5} style={{ padding: '0 10px 9px', borderBottom: '1px solid var(--bm-border)' }}>
            <div style={{
              display: 'block',
              fontSize: 12, lineHeight: 1.5,
              color: 'var(--bm-threat-2, #fb923c)',
              background: 'var(--bm-threat-2-bg, rgba(251,146,60,0.14))',
              borderRadius: 6, padding: '7px 10px',
            }}>
              <span style={{
                fontFamily: 'var(--bm-font-mono)', fontSize: 9.5, fontWeight: 700,
                letterSpacing: '.4px', marginRight: 8,
              }}>⚑ Minority view{o.dissentingExpert ? ` · ${o.dissentingExpert}` : ''}</span>
              {o.dissent}
            </div>
          </td>
        </tr>
      ) : (
        <tr>
          <td />
          <td colSpan={5} style={{ padding: '0 10px 9px', borderBottom: '1px solid var(--bm-border)' }}>
            <div style={{
              fontSize: 11.5, lineHeight: 1.4,
              color: enforceDissentSurfacing ? 'var(--bm-threat-3, #fbbf24)' : 'var(--bm-text-subtle)',
              background: enforceDissentSurfacing ? 'var(--bm-prov-stale-bg, rgba(251,191,36,0.14))' : 'transparent',
              borderRadius: 6, padding: enforceDissentSurfacing ? '5px 10px' : '0',
              fontStyle: 'italic' as const,
            }}>
              {enforceDissentSurfacing ? '⚠ ' : ''}No minority view captured for this option
              {enforceDissentSurfacing ? ' — confirm with originating expert / committee before publishing' : ''}.
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
